import type { World } from '../sim/types';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { isCleanupHome } from '../sim/operations';
import { shoreDebrisT } from '../sim/shoreDebris';
import { NET_DRAWN_T, shoreNets } from './derelictGear';
import { epochDayOf } from '../sim/calendar';
import { patrolCut, roughly } from './actionWorth';
import { escapeHtml } from './format';

/**
 * Results the reef shows (M332, `docs/ONE_REEF_ROADMAP.md`'s O7, the playtest's "accomplishments shown in the habitat and
 * its animals"): what changes in weeks, counted where the player looks, beside the reef's scene in Explore. GAME CHOICE:
 * the scene is the reef under water, where no shore and no net is drawn (the art is the user's, M75), so the nets
 * cleared are counted beside it and on the shore's own place (M328's places); the grazing fish the patrols keep are the
 * world's own catch, at the patrols as they stand against none (`patrolCut`, an ESTIMATE on today's fish); the coral's
 * slow answer is left to the surveys, as the simulation has it (M47).
 */
export interface ReefWork {
  /** The nets the conservancy's cleanups took off the shores in the last year, tonnes, and the trips. */
  netsT: number;
  trips: number;
  /** The shore holding the most nets now, and its tonnes. */
  worst: { island: ReefIsland; tonnes: number };
  /** The grazing fish kept on the reef a month by the patrols running, kg, and the coasts patrolled. */
  keptKg: number;
  patrolled: ReefIsland[];
}

export function reefWork(world: World): ReefWork {
  const today = epochDayOf(world);
  const cleanups = world.operations.finished.filter((f) => isCleanupHome(f) && today - f.endDay < 365);
  const m = world.sanctuaries[0]?.management;
  const patrolled = m ? REEF_ISLANDS.filter((_, i) => (m.patrols[i] ?? 0) > 0) : [];
  const keptKg = patrolled.reduce((n, island) => n + (patrolCut(world, island, m?.patrols[REEF_ISLANDS.indexOf(island)] ?? 0)?.fewer ?? 0), 0);
  const worst = REEF_ISLANDS.map((island) => ({ island, tonnes: shoreDebrisT(world, island) })).sort((a, b) => b.tonnes - a.tonnes)[0] as { island: ReefIsland; tonnes: number };
  return { netsT: cleanups.reduce((n, f) => n + (f.tonnes ?? 0), 0), trips: cleanups.length, worst, keptKg, patrolled };
}

/** "Your work on the reef", beside the scene in Explore: the nets cleared and left, the fish the patrols keep. */
export function reefWorkHtml(world: World): string {
  const w = reefWork(world);
  const lines = [
    w.trips > 0 ? `${w.netsT.toFixed(1)} t of derelict nets taken off the shores in the last year, ${w.trips} ${w.trips === 1 ? 'trip' : 'trips'}; ${Math.round(w.worst.tonnes)} t still lie on ${ISLAND_NAMES[w.worst.island]}'s, the most.` : `No cleanup in the last year: ${Math.round(w.worst.tonnes)} t of derelict nets lie on ${ISLAND_NAMES[w.worst.island]}'s shore, the most.`,
    // A month's catch the patrols save at today's fish (`patrolCut`, an ESTIMATE), told as that (its review: "kept on
    // the reef" and "in the water here" had told more than the figure is, whatever habitat is on the screen).
    w.patrolled.length > 0 ? `About ${roughly(w.keptKg)} kg a month fewer parrotfish, kala, nenue and manini taken off Maui Nui's reef, at today's fish, under your patrols on ${w.patrolled.map((i) => ISLAND_NAMES[i]).join(', ')}.` : 'No patrol running: the grazing fish are taken at Hawaiʻi\'s own coverage.',
  ];
  return `<section class="reef-work"><h3>Your work on the reef</h3><ul class="rows">${lines.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul>${shoreNetsHtml(world)}</section>`;
}

/**
 * Each shore's nets, drawn (M338, `docs/CLARITY_ROADMAP.md`'s C6): a net sprite for every `NET_DRAWN_T` on it today, so a
 * cleanup home takes one away (`shoreNets`, its GAME CHOICE there). The nets the user's (`art/derelict-net`, M337).
 */
/** A shore told clear below this, tonnes: its figure reads 0.0 t (JUDGED: what the strip's one decimal shows as none). */
export const CLEAR_T = 0.05;

export function shoreNetsHtml(world: World): string {
  const rows = REEF_ISLANDS.map((island) => {
    const tonnes = shoreDebrisT(world, island);
    const drawn = shoreNets(world, island);
    const n = drawn.length;
    const nets = drawn.map((p) => `<canvas data-gear="${p.kind}" data-look="${p.fouled ? 'fouled' : 'clean'}" data-px="${p.kind === 'large' ? 52 : 36}"></canvas>`).join('');
    return `<li><span class="shore-name">${escapeHtml(ISLAND_NAMES[island])}'s shore, ${tonnes.toFixed(1)} t</span><div class="shore-nets"${n === 0 ? ' data-clear="1"' : ''}>${nets || `<span class="row-note">${tonnes < CLEAR_T ? 'Clear for now' : 'Less than one net\'s worth'}</span>`}</div></li>`;
  }).join('');
  return `<h4>Nets on the shores</h4><ul class="rows shore-strips">${rows}</ul><p class="row-note">${escapeHtml(`Each net drawn is about ${NET_DRAWN_T.toFixed(1)} t, one cleanup trip's haul.`)}</p>`;
}

/** Where a result is seen (M332): a line and the button to Explore, for a result's card or a Done line. */
const SEE_IT: Readonly<Record<'cleanup' | 'patrol' | 'survey', string>> = {
  cleanup: 'See it in Explore, your work on the reef.',
  patrol: 'See the catch it saves in Explore, your work on the reef.',
  survey: 'See the counts in Explore, beside the reef.',
};
export const seeItHtml = (kind: keyof typeof SEE_IT): string => `<p class="row-note see-it">${escapeHtml(SEE_IT[kind])} <button class="link" data-act="goal" data-goal="explore">Explore</button></p>`;
